
import { useState, useCallback } from 'react';
import { Program } from '@/services/db';
import { toast } from 'sonner';
import { CertificateMetadata } from '@/types/certification';

interface UseCertificationLogicProps {
  studentId: number;
  programs: Program[];
  issueProgramCertificate: (
    studentId: number, 
    programTitle: string, 
    programCategory: string, 
    issuedBy: string
  ) => CertificateMetadata | null;
  verifyPrerequisites: (
    studentId: number,
    programTitle: string,
    programCategory: string
  ) => { met: boolean };
}

export const useCertificationLogic = ({
  studentId,
  programs,
  issueProgramCertificate,
  verifyPrerequisites
}: UseCertificationLogicProps) => {
  const [selectedProgram, setSelectedProgram] = useState<string | null>(
    programs.length > 0 ? `${programs[0].title}:${programs[0].category}` : null
  );
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState<string>('course-completion');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const isProgramCompleted = useCallback((programTitle: string) => {
    if (!programTitle) return false;
    
    const program = programs.find(p => p.title === programTitle);
    if (!program) return false;
    
    if (!program.completionDate) return false;
    
    const completionDate = new Date(program.completionDate);
    const currentDate = new Date();
    return !isNaN(completionDate.getTime()) && completionDate <= currentDate;
  }, [programs]);

  const handleIssueCertificate = useCallback(() => {
    if (!selectedProgram) {
      toast.error('No program selected');
      return;
    }
    
    const [programTitle, programCategory] = selectedProgram.split(':');
    
    if (!isProgramCompleted(programTitle)) {
      toast.error('Cannot issue certificate: Program not yet completed');
      return;
    }
    
    const prerequisiteCheck = verifyPrerequisites(studentId, programTitle, programCategory);
    
    if (!prerequisiteCheck.met) {
      toast.error('Cannot issue certificate: Prerequisites not met');
      return;
    }
    
    setSelectedTemplateType('course-completion');
    setSelectedTemplateId(null); // Reset template selection
    setIsTemplateDialogOpen(true);
  }, [selectedProgram, isProgramCompleted, verifyPrerequisites, studentId]);

  const handleConfirmCertificateIssue = useCallback(() => {
    if (!selectedProgram || !selectedTemplateId) {
      toast.error('Template not selected');
      return;
    }
    
    const [programTitle, programCategory] = selectedProgram.split(':');
    
    const certificate = issueProgramCertificate(
      studentId, 
      programTitle, 
      programCategory, 
      'System Administrator'
    );
    
    setIsTemplateDialogOpen(false);
    
    if (certificate) {
      toast.success(`Certificate issued for ${programTitle}`);
    } else {
      toast.error('Failed to issue certificate');
    }
  }, [selectedProgram, selectedTemplateId, issueProgramCertificate, studentId]);

  return {
    selectedProgram,
    setSelectedProgram,
    isTemplateDialogOpen,
    setIsTemplateDialogOpen,
    selectedTemplateType,
    setSelectedTemplateType,
    selectedTemplateId,
    setSelectedTemplateId,
    isProgramCompleted,
    handleIssueCertificate,
    handleConfirmCertificateIssue
  };
};
